import React, { useEffect, useState } from 'react';
import { DrillFile } from "../typings";
import { Align, Row } from "../components/Flex";
import { deleteDrill, fetchDrills } from "./drills-api";
import DrillRow from "./DrillRow";
import TextField from "../components/TextField";
import { FaAngleLeft, FaAngleRight, FaSort, FaSortDown, FaSortUp } from "react-icons/fa6";
import { useAuth } from "../auth-context";
import { toast } from "react-toastify";
import { useApi } from "../api-context";

function DrillFilesPage() {
    const api = useApi();
    const [total, setTotal] = useState(0);
    const [drills, setDrills] = useState<DrillFile[]>([]);

    const [page, setPage] = useState(0);
    const [nameFilter, setNameFilter] = useState('');
    const [orderBy, setOrderBy] = useState('id');
    const [orderDirection, setOrderDirection] = useState('ASC');

    const loadDrills = (page: number, orderBy: string, orderDirection: string, nameFilter?: string) => {
        fetchDrills(api, { orderBy, orderDirection, page, nameFilter })
            .then(res => {
                setTotal(res.total);
                setDrills(res.drills);
            })
            .catch((reason) => {
                if (reason !== 'Unauthorized') {
                    toast.error('Failed to load files');
                }
            });
    };

    const onDelete = (id: number) => {
        deleteDrill(api, id)
            .then(() => {
                setPage(0);
                loadDrills(page, orderBy, orderDirection, nameFilter || undefined);
                toast.success("Successfully removed file");
            })
            .catch(() => toast.error("Failed to remove file!"))
    };

    const onFilter = (nameFilter: string) => {
        setPage(0);
        setNameFilter(nameFilter);
    }

    const onChangeOrder = (newOrderBy: string) => {
        setPage(0);
        if (newOrderBy === orderBy) {
            setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
            return;
        }
        setOrderBy(newOrderBy);
        setOrderDirection('ASC')
    }

    useEffect(() => {
        loadDrills(page, orderBy, orderDirection, nameFilter || undefined);
    }, [orderBy, orderDirection, nameFilter, page]);

    const renderColumnHeader = (title: string, prop: string, flex: number) => {
        return (
            <div style={{ flex, display: 'flex' }}>
                <div className="column-header" onClick={() => onChangeOrder(prop)}>
                    <div>{title}</div>
                    {
                        orderBy !== prop
                            ? <FaSort/>
                            : orderDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />
                    }
                </div>
            </div>
        );
    };

    const renderSearchHeader = () => {
        return (
            <Row align={Align.SPACE_BETWEEN} className="search-header">
                <TextField label="" value={nameFilter} setValue={onFilter} placeholder="Search by name"/>
                <div style={{ color: '#545452' }}>
                    {`Showing ${page * 10 + (total ? 1 : 0)}-${Math.min(total, (page + 1) * 10)} of ${total}`}
                </div>
            </Row>
        );
    };

    return (
        <div className="page-container drill-files-page">
            <div className="main-title">
                Files
            </div>
            {renderSearchHeader()}
            <Row style={{ padding: '12px 16px', marginBottom: '12px' }}>
                {renderColumnHeader('Name', 'name', 2)}
                {renderColumnHeader('Uploaded at', 'created', 1)}
                {renderColumnHeader('Last edited', 'edited', 1)}
                {renderColumnHeader('Last edited by', 'email', 2)}
                <div style={{ width: '28px' }}/>
            </Row>
            <div className="content-container" style={{ marginBottom: '32px', gap: '6px' }}>
                {drills.map((drill) => (
                    <DrillRow
                        key={drill.id}
                        api={api}
                        drill={drill}
                        onRemove={onDelete}
                    />
                ))}
            </div>
            <Row align={Align.END} gap={4}>
                <FaAngleLeft
                    className="pager-icon"
                    onClick={() => setPage(Math.max(0, page - 1))}
                    size={20}
                    color="#545452"
                />
                <div style={{
                    fontSize: '16px',
                    fontFamily: 'InterRegular',
                    color: '#545452'
                }}>
                    {page + 1} of {Math.ceil(total / 10)}
                </div>
                <FaAngleRight
                    className="pager-icon"
                    size={20}
                    onClick={() => setPage(Math.min(Math.floor(total / 10), page + 1))}
                    color="#545452"
                />
            </Row>
        </div>
    );
}

export default DrillFilesPage;
